/**
 * กฎราคาทุกช่องทางจากราคา Shopee General4 — ตัดสินใจ 2026-10-26
 *
 * ⚠️ สำเนาของ `lnw-crawler-v2/src/crawlers/price_update_crawler/channel_align.ts` (RULES) ซึ่งเป็น
 * เจ้าของกฎและใช้จัดราคาทั้งแคตตาล็อก — แก้กฎที่นั่นก่อน แล้วแก้ที่นี่ให้ตรงในคอมมิตเดียวกัน ถ้าสองที่
 * ไม่ตรงกัน ราคาที่ตั้งจากหน้าสินค้าจะเพี้ยนจากราคาที่รอบจัดระเบียบตั้งไว้ (เลข instance อ่านจาก
 * `pdata` ของ lnwshop เอง ไม่ใช่จาก registry กลาง)
 *
 *   Shopee General4 (1005)              = ตัวตั้ง P
 *   Shopee genlogic / Lazada gen4.group = P × 1.02 ปัดขึ้นเป็นบาท
 *   TikTok                              = P ขั้นต่ำ 30 บาท
 *   Lazada genlogic                     = P ÷ 1.07 (ถอด VAT — ร้าน pensri ไม่จด VAT) ปัดบาทใกล้สุด
 *   LINE Shopping                       = ตามราคาหลัก LnwShop (โหมด sync ไม่มีตัวเลขของตัวเอง)
 *
 * ส่วน "ราคาหลัก → General4" เป็นของหน้า price-set อย่างเดียว (รอบจัดระเบียบเริ่มจาก General4 ที่มีอยู่แล้ว):
 * สูตรเดียวกับเครื่องคิดเลข — ราคาหลักหัก VAT อย่างเดียว ราคาแพลตฟอร์มหักค่าธรรมเนียม + VAT
 */

export const GENERAL4 = {
    channel: 'shopee',
    instance: '1005',
    label: 'Shopee General4',
};

export interface ChannelPriceRule {
    channel: string; // key ใน pdata.props.channel_prices / channel_urls / infos.channel
    instance: string; // instance id ของ lnwshop
    label: string;
    rule: string; // คำอธิบายสั้นไว้โชว์
    kind: 'set' | 'sync'; // sync = ตามราคาหลัก LnwShop
    price: (general4: number, base: number) => number;
}

export const CHANNEL_PRICE_RULES: ChannelPriceRule[] = [
    {
        channel: 'lazada',
        instance: '1003',
        label: 'Lazada genlogic',
        rule: '÷ 1.07',
        kind: 'set',
        price: (p) => Math.round(p / 1.07 + 1e-9),
    },
    {
        channel: 'lazada',
        instance: '100001',
        label: 'Lazada gen4.group',
        rule: '× 1.02',
        kind: 'set',
        price: (p) => Math.ceil(p * 1.02 - 1e-9),
    },
    {
        channel: 'shopee',
        instance: '100002',
        label: 'Shopee genlogic',
        rule: '× 1.02',
        kind: 'set',
        price: (p) => Math.ceil(p * 1.02 - 1e-9),
    },
    {
        channel: 'tiktok_shop',
        instance: '1010',
        label: 'TikTok',
        rule: 'ขั้นต่ำ 30',
        kind: 'set',
        price: (p) => Math.max(p, 30),
    },
    {
        channel: 'line_shopping',
        instance: '1009',
        label: 'LINE Shopping',
        rule: 'ตามราคาหลัก',
        kind: 'sync',
        price: (_p, base) => base,
    },
];

/**
 * General4 จากราคาหลัก — หลักการเดียวกับ "ราคาขาย PF" เทียบ "ราคาขาย LS" ในเครื่องคิดเลข: ทั้งคู่
 * gross-up จากยอดเดียวกัน ราคาหลักหัก VAT อย่างเดียว ราคาแพลตฟอร์มหักค่าธรรมเนียม + VAT
 * (VAT อยู่ในราคาแล้วจึงหักที่ vat/(100+vat) ไม่ใช่ vat) · ปัดขึ้นเป็นบาท
 * คืน null เมื่อค่าธรรมเนียมกินทั้งราคา (ไม่มีราคาจำกัดที่คุ้ม)
 */
export function general4FromBase(
    base: number,
    platformFeePct: number,
    vatPct: number,
): number | null {
    const vat = vatPct / (100 + vatPct);
    const platformShare = 1 - platformFeePct / 100 - vat;
    if (!(base > 0) || platformShare <= 0) return null;
    return Math.ceil((base * (1 - vat)) / platformShare - 1e-9);
}

export interface ChannelPricePlan {
    channel: string;
    instance: string;
    label: string;
    rule: string;
    kind: 'set' | 'sync';
    listed: boolean; // มีลิงก์สินค้าบน marketplace (ไม่มี = ตั้งค้างใน lnwshop อย่างเดียว)
    current: number | null; // ราคา effective ตอนนี้ (null = อ่านไม่ได้/เป็นช่วง)
    next: number; // ราคาตามกฎ (sync = ราคาหลักใหม่)
    changed: boolean;
}

/** `pdata` ของหน้าสินค้า lnwshop — เท่าที่หน้า price-set ใช้ */
export interface LnwPdata {
    id?: number | string;
    props?: {
        code?: string;
        price?: unknown;
        can_edit_price?: unknown;
        channel_prices?: Record<string, unknown>;
        channel_urls?: Record<string, unknown>;
    };
    infos?: { channel?: { key: string; show: unknown }[] };
}

const num = (v: unknown): number | null => {
    // typeof object กันไว้: Number([38]) = 38 แต่มันคือช่วง ไม่ใช่ราคา · "" = ไม่มี override
    if (v == null || v === '' || typeof v === 'object') return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
};

const at = (
    m: Record<string, unknown> | undefined,
    ch: string,
    inst: string,
): unknown => {
    const x = m?.[ch];
    return x && typeof x === 'object' && !Array.isArray(x)
        ? (x as Record<string, unknown>)[inst]
        : undefined;
};

/** ราคา effective ของช่อง — ไม่มี entry ใน channel_prices = ตามราคาหลัก */
export function effectivePrice(
    pdata: LnwPdata,
    channel: string,
    instance: string,
): number | null {
    const raw = at(pdata.props?.channel_prices, channel, instance);
    return raw === undefined || raw === '' ? num(pdata.props?.price) : num(raw);
}

export function isListed(
    pdata: LnwPdata,
    channel: string,
    instance: string,
): boolean {
    return !!at(pdata.props?.channel_urls, channel, instance);
}

/** สินค้ามีแท็บของช่องนี้ไหม (infos.channel[].show) */
export function hasChannelTab(
    pdata: LnwPdata,
    channel: string,
    instance: string,
): boolean {
    const show = pdata.infos?.channel?.find((c) => c.key === channel)?.show;
    return Array.isArray(show) && show.map(String).includes(instance);
}

/** แผนราคาทุกช่องจาก (ราคาหลักใหม่, General4 ใหม่) — เฉพาะช่องที่สินค้ามีแท็บ */
export function planChannelPrices(
    pdata: LnwPdata,
    base: number,
    general4: number,
): ChannelPricePlan[] {
    return CHANNEL_PRICE_RULES.filter((r) =>
        hasChannelTab(pdata, r.channel, r.instance),
    ).map((r) => {
        const current = effectivePrice(pdata, r.channel, r.instance);
        const next = r.price(general4, base);
        return {
            channel: r.channel,
            instance: r.instance,
            label: r.label,
            rule: r.rule,
            kind: r.kind,
            listed: isListed(pdata, r.channel, r.instance),
            current,
            next,
            changed: current === null || Math.abs(current - next) >= 0.005,
        };
    });
}
